import * as React from "react";
import { useHistory } from "react-router-dom";
import { CornerDownLeft, LogOut, Moon, Search, Sun } from "Components/ui/icons";

import { Dialog, DialogContent, DialogHeader, Input, Kbd, notify } from "Components/ui";
import { useTheme } from "context/ThemeContext";
import { useAuth } from "hooks/useAuth";
import { cn } from "utils/cn";
import { useVisibleNavigation } from "./SidebarNav";

interface Command {
  id: string;
  label: string;
  group: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords: string;
  run: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Cmd/Ctrl+K: jump to any page the user can open, switch theme or sign out. */
export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange }) => {
  const history = useHistory();
  const groups = useVisibleNavigation();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { signOut } = useAuth();
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);

  const commands = React.useMemo<Command[]>(() => {
    const pages: Command[] = groups.flatMap((group) =>
      group.items.map((item) => ({
        id: `page-${item.path}`,
        label: item.label,
        group: "Pages",
        hint: group.label,
        icon: item.icon,
        keywords: [item.label, group.label, ...(item.keywords || [])].join(" ").toLowerCase(),
        run: () => history.push(item.path),
      }))
    );
    const actions: Command[] = [
      {
        id: "action-theme",
        label: resolvedTheme === "dark" ? "Switch to light theme" : "Switch to dark theme",
        group: "Actions",
        icon: resolvedTheme === "dark" ? Sun : Moon,
        keywords: "theme dark light mode appearance",
        run: toggleTheme,
      },
      {
        id: "action-sign-out",
        label: "Sign out",
        group: "Actions",
        icon: LogOut,
        keywords: "sign out log out logout exit",
        run: () => {
          signOut();
          notify.success("Signed out");
          history.push("/credentials");
        },
      },
    ];
    return [...pages, ...actions];
  }, [groups, history, resolvedTheme, toggleTheme, signOut]);

  const results = React.useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    return terms.length
      ? commands.filter((c) => terms.every((t) => c.keywords.includes(t)))
      : commands;
  }, [commands, query]);

  React.useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  React.useEffect(() => setActiveIndex(0), [query]);

  React.useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const execute = (command: Command | undefined) => {
    if (!command) return;
    onOpenChange(false);
    command.run();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (results.length ? (index + 1) % results.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        results.length ? (index - 1 + results.length) % results.length : 0
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      execute(results[activeIndex]);
    }
  };

  const optionId = (index: number) => `command-option-${index}`;
  let lastGroup = "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        hideClose
        className="top-[15vh] translate-y-0 gap-0 overflow-hidden p-0"
      >
        <DialogHeader title="Search SAP" className="sr-only" />
        <div className="border-b border-border">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search pages and actions…"
            aria-label="Search pages and actions"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-results"
            aria-activedescendant={results.length ? optionId(activeIndex) : undefined}
            autoComplete="off"
            spellCheck={false}
            leading={<Search />}
            className="h-14 rounded-none border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
          />
        </div>

        <div
          ref={listRef}
          id="command-results"
          role="listbox"
          aria-label="Results"
          className="max-h-[22rem] overflow-y-auto p-2"
        >
          {results.length === 0 && (
            <p className="px-3 py-10 text-center text-sm text-muted-foreground">
              No pages or actions match “{query}”.
            </p>
          )}
          {results.map((command, index) => {
            const heading = command.group !== lastGroup ? command.group : null;
            lastGroup = command.group;
            const Icon = command.icon;
            const active = index === activeIndex;
            return (
              <React.Fragment key={command.id}>
                {heading && (
                  <p className="px-3 pb-1.5 pt-3 text-[11px] font-medium uppercase tracking-wider text-subtle-foreground first:pt-1">
                    {heading}
                  </p>
                )}
                <div
                  id={optionId(index)}
                  role="option"
                  aria-selected={active}
                  data-index={index}
                  onMouseMove={() => !active && setActiveIndex(index)}
                  onClick={() => execute(command)}
                  className={cn(
                    "flex h-10 cursor-pointer select-none items-center gap-3 rounded-control px-3 text-sm",
                    active ? "bg-surface-muted text-foreground" : "text-muted-foreground"
                  )}
                >
                  <Icon
                    className={cn(
                      "h-4 w-4 shrink-0",
                      active ? "text-primary-text" : "text-subtle-foreground"
                    )}
                  />
                  <span className="flex-1 truncate">{command.label}</span>
                  {command.hint && (
                    <span className="text-xs text-subtle-foreground">{command.hint}</span>
                  )}
                  {active && (
                    <CornerDownLeft
                      aria-hidden="true"
                      className="h-3.5 w-3.5 text-subtle-foreground"
                    />
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div className="flex items-center gap-4 border-t border-border bg-surface-muted/50 px-4 py-2.5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> to move
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>↵</Kbd> to open
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>esc</Kbd> to close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Opens the palette on Cmd+K (macOS) or Ctrl+K. */
export const useCommandPaletteShortcut = (toggle: () => void) => {
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);
};
